import { apiClient } from '@/lib/api/client';
import type {
  SellerProfile,
  Company,
  Subscription,
  Preferences,
  UpdateProfileData,
  UpdatePasswordData,
  UpdateEmailData,
  DeleteAccountData,
  UpdatePreferencesData,
  NotificationsResponse,
  AvailablePlan,
} from '../types/settings.types';

interface ApiData<T> {
  data: T;
}

export const settingsService = {
  // Profile - uses /seller/me and /seller/* endpoints
  async getProfile(): Promise<SellerProfile | null> {
    const response = await apiClient.get<ApiData<SellerProfile>>('/seller/me');
    return response.data?.data ?? null;
  },

  async updateProfile(data: UpdateProfileData): Promise<SellerProfile | null> {
    const response = await apiClient.put<ApiData<SellerProfile>>('/seller/account/update', data);
    return response.data?.data ?? null;
  },

  async updatePassword(data: UpdatePasswordData): Promise<void> {
    await apiClient.put('/seller/password/update', data);
  },

  async updateEmail(data: UpdateEmailData): Promise<void> {
    await apiClient.put('/seller/email/update', data);
  },

  // Company - read-only endpoint
  async getCompany(): Promise<Company | null> {
    const response = await apiClient.get<ApiData<Company>>('/company/seller/info');
    return response.data?.data ?? null;
  },

  // Notifications - uses /notifications/* endpoints
  async getNotifications(page = 1, limit = 20): Promise<NotificationsResponse> {
    const response = await apiClient.get<ApiData<NotificationsResponse>>(
      `/notifications/seller?page=${page}&limit=${limit}`
    );
    return response.data?.data ?? { items: [], total: 0 };
  },

  // The backend currently exposes `@Put('read')` in
  // SendMessage/controllers/appNotification.controller.ts but it tries to read
  // `@Param('id')` without declaring `:id` in the route — buggy server-side.
  // The semantically correct URL is kept here; once the back is patched to
  // `@Put(':id/read')` (or `@Put('read/:id')`), this call will work as-is.
  async markNotificationAsRead(notificationId: string): Promise<void> {
    await apiClient.put(`/notifications/${notificationId}/read`);
  },

  // No bulk "mark all as read" endpoint exists on the backend, so we fall back
  // to a client-side loop over unread notifications. Errors on individual
  // items don't abort the batch — we use `allSettled` and surface a single
  // result. The caller is expected to invalidate the notifications query on
  // success so the UI re-renders with the updated state.
  async markAllNotificationsAsRead(): Promise<void> {
    // Pull a generous page; if the user has more than 100 unread the bell
    // counter will simply require another click — acceptable for a fallback.
    const list = await this.getNotifications(1, 100);
    const unreadIds = (list.items ?? []).filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await Promise.allSettled(unreadIds.map((id) => this.markNotificationAsRead(id)));
  },

  async savePushToken(token: string): Promise<void> {
    await apiClient.post('/notifications/token/seller/save', { token });
  },

  // Subscription - uses /payment/volovolo/* endpoints
  async getSubscription(): Promise<Subscription | null> {
    const response = await apiClient.get<ApiData<Subscription>>(
      '/payment/volovolo/subscriptions/current'
    );
    return response.data?.data ?? null;
  },

  async cancelSubscription(): Promise<void> {
    await apiClient.post('/payment/volovolo/subscriptions/cancel');
  },

  async getAvailablePlans(): Promise<AvailablePlan[]> {
    const response = await apiClient.get<ApiData<AvailablePlan[]>>(
      '/payment/volovolo/seller-plans'
    );
    return response.data?.data ?? [];
  },

  async createSubscriptionIntent(planId: string): Promise<{ clientSecret: string } | null> {
    const response = await apiClient.post<ApiData<{ clientSecret: string }>>(
      '/payment/volovolo/subscription-intent',
      { planId }
    );
    return response.data?.data ?? null;
  },

  async verifyIAPSubscription(receipt: string, platform: 'ios' | 'android'): Promise<Subscription | null> {
    const response = await apiClient.post<ApiData<Subscription>>(
      '/payment/volovolo/verify-iap-subscription',
      { receipt, platform }
    );
    return response.data?.data ?? null;
  },

  // Preferences - stored locally or through seller update
  async getPreferences(): Promise<Preferences> {
    const profile = await this.getProfile();
    return {
      language: profile?.language || 'fr',
      currency: 'DZD',
      dateFormat: 'dd/MM/yyyy',
      theme: 'system',
    };
  },

  async updatePreferences(data: UpdatePreferencesData): Promise<Preferences> {
    if (data.language) {
      await apiClient.put('/seller/account/update', { language: data.language });
    }
    return {
      language: data.language || 'fr',
      currency: data.currency || 'DZD',
      dateFormat: data.dateFormat || 'dd/MM/yyyy',
      theme: data.theme || 'system',
    };
  },

  // Account deletion
  async deleteAccount(data: DeleteAccountData): Promise<void> {
    await apiClient.delete('/seller/account/delete', {
      data: { password: data.password, reason: data.reason },
    });
  },
};
